/* global google */

import React, { useRef, useEffect } from 'react';
import useWindowSize from 'shared/hooks/useWindowSize';
import { H2Alt } from 'scy/text';
import { textCenter } from 'scy/utils.mod.scss';
import { WideContainer } from 'scy/layouts';
import padding from 'scy/padding.mod.scss';
import styles from './styles.mod.scss';

function useGoogleMap(containerRef, { lat, lng }) {
  const mapRef = useRef();

  useEffect(() => {
    if (containerRef.current && !!lat && !!lng) {
      mapRef.current = new google.maps.Map(containerRef.current, {
        zoom: 11,
        minZoom: 10,
        maxZoom: 15,
        zoomControl: true,
        controlSize: 20,
        disableDefaultUI: true,
        clickableIcons: false,
        center: { lat, lng }
      });
    }

    return function cleanupMap() {
      mapRef.current = null;
    };
  }, [containerRef, lat, lng]);

  return mapRef;
}

function forEachPolygon(polygons, fn) {
  return polygons.forEach(polygon => {
    // sometimes coordinates are nested one layer deeper
    if (polygon[0] && polygon[0][0] && polygon[0][0].length) {
      polygon = polygon[0];
    }
    return fn(polygon);
  });
}

function useGoogleMapPolygons(mapRef, coordinates, { width, height }) {
  const polygonRefs = useRef([]);

  useEffect(() => {
    forEachPolygon(coordinates, polygon => {
      let path = [];
      polygon.forEach(([lng, lat]) => {
        if (typeof lat !== 'number' || typeof lng !== 'number') return;
        path.push({ lat, lng });
      });

      if (mapRef.current && path.length) {
        const polygon = new google.maps.Polygon({
          path,
          map: mapRef.current,
          clickable: false,
          strokeColor: '#FA6446',
          strokeWeight: 3,
          fillColor: '#FCB1A2',
          fillOpacity: 0.5
        });
        polygonRefs.current.push(polygon);
      }
    });

    let minLat, minLng, maxLat, maxLng;
    forEachPolygon(coordinates, polygon => {
      polygon.forEach(([lng, lat]) => {
        if (typeof lat !== 'number' || typeof lng !== 'number') return;
        if (!minLat || lat < minLat) minLat = lat;
        if (!minLng || lng < minLng) minLng = lng;
        if (!maxLat || lat > maxLat) maxLat = lat;
        if (!maxLng || lng > maxLng) maxLng = lng;
      });
    });

    if (
      mapRef.current &&
      typeof minLat === 'number' &&
      typeof minLng === 'number' &&
      typeof maxLat === 'number' &&
      typeof maxLng === 'number'
    ) {
      const bounds = new google.maps.LatLngBounds(
        { lat: minLat, lng: minLng },
        { lat: maxLat, lng: maxLng }
      );
      const padding = -50;
      mapRef.current.fitBounds(bounds, padding);
    }

    return function cleanupPolygons() {
      polygonRefs.current.forEach(polygon => {
        polygon.setMap(null);
      });
      polygonRefs.current = [];
    };
  }, [mapRef, width, height, coordinates]);
}

export default function CoverageMap({ city }) {
  const { width, height } = useWindowSize();

  const mapContainer = useRef();

  const center = city.center || {};
  const [lng, lat] = center.coordinates || [];
  const mapRef = useGoogleMap(mapContainer, { lat, lng });

  const polygon = city.polygon || {};
  const coordinates = polygon.coordinates || [];
  useGoogleMapPolygons(mapRef, coordinates, { width, height });

  if (!lng || !lat || !coordinates || !coordinates.length) return null;

  return (
    <WideContainer tag="section" className={textCenter}>
      <div className={padding.lg}>
        <H2Alt>{city.name} Coverage Map</H2Alt>
      </div>
      <div ref={mapContainer} className={styles.map} />
    </WideContainer>
  );
}
